<script setup lang="ts">
import { useBonusService } from '~/composables/services/useBonusService';
import type {
  CasinoBonus,
  CasinoBonusStack,
} from '~/interfaces/dto/bonus/response';
import Button from '~/components/design-system/button.vue';
import { useAuthStore } from '~/stores/auth.store';
import { ArrowPathIcon } from '@heroicons/vue/24/outline';
import ExternalIcon from '~/components/design-system/icons/ExternalIcon.vue';

const auth = useAuthStore();
const site = useSiteStore();
const { $trackEvent } = useNuxtApp();
const router = useRouter();
const bonusService = useBonusService();

const bonus = ref<CasinoBonusStack | null>(null);
const bonusArchive = ref<CasinoBonus[] | null>(null);

const potentialForfeit = ref<string | null>(null);

const loading = ref<boolean>(true);

async function getBonusWallet() {
  try {
    loading.value = true;
    bonus.value = null;
    bonusArchive.value = null;
    await bonusService
      .getBonusStack()
      .then((data) => {
        loading.value = false;
        bonus.value = data;
      })
      .catch((e) => {
        loading.value = false;
        console.error(e);
      });
    const today = new Date();
    const archiveTime = new Date().setMonth(today.getMonth() - 6);
    await bonusService
      .getArchiveBonusStack({
        toDate: today.toISOString().split('T')[0],
        fromDate: new Date(archiveTime).toISOString().split('T')[0],
      })
      .then((data) => {
        bonusArchive.value = data;
        loading.value = false;
      })
      .catch((e) => {
        loading.value = false;
        console.error(e);
      });
  } catch (e) {
    loading.value = false;
    console.error(e);
  }
}

onMounted(async () => {
  await getBonusWallet();
  $trackEvent('Account', 'MY_ACCOUNT_BONUS_WALLET');
});

function setPotentialForfeit(id: string) {
  potentialForfeit.value = id;
}

async function forfeitBonus(id: string) {
  loading.value = true;
  await bonusService
    .forfeitBonus(id)
    .then(async () => {
      bonus.value = null;
      potentialForfeit.value = null;
    })
    .finally(
      async () =>
        await bonusService.getBonusStack().then((data) => {
          bonus.value = data;
          loading.value = false;
          $trackEvent('Account', 'MY_ACCOUNT_BONUS_WALLET_FORFEIT');
        }),
    );
}

async function refreshBonusWallet() {
  await getBonusWallet();
  $trackEvent('Account', 'MY_ACCOUNT_BONUS_WALLET_REFRESH');
}

function howToClicked() {
  auth.toggleAccountModal(false);
  site.deactivateModal();
  router.push('/faq?accordion=accordionBonusWallet');
  $trackEvent('Account', 'MY_ACCOUNT_BONUS_WALLET_HOW_TO');
}

const modalController = computed({
  get: () => !!potentialForfeit.value,
  set: (value) => {
    !value ? (potentialForfeit.value = null) : '';
  },
});
</script>
<template>
  <div>
    <LazyDesignSystemModal
      v-model:controller="modalController"
      :title="$t('forfeit-bonus-title')"
      @close="potentialForfeit = null"
    >
      <template #modal-content>
        <div class="py-2 flex items-center justify-center text-base-priority">
          {{ $t('forfeit-bonus-confirmation') }}
        </div>
        <div class="grid grid-cols-2 gap-2 p-3 bg-light-200 dark:bg-dark-900">
          <Button
            element-name="back-to-bonus-wallet"
            type="primary"
            class="justify-center"
            @click="potentialForfeit = null"
          >
            {{ $t('back-to-bonus-wallet') }}
          </Button>
          <Button
            element-name="forfeit-bonus"
            type="secondary"
            class="justify-center"
            @click="forfeitBonus(potentialForfeit)"
          >
            {{ $t('proceed') }}
          </Button>
        </div>
      </template>
    </LazyDesignSystemModal>
    <UserSettingsBaseContainer stacked miniContainer class="!p-0 sm:mt-4">
      <div class="flex justify-between items-center p-2">
        <p class="text-base-priority font-bold ml-2">
          {{ $t('bonus-wallet') }}
        </p>
        <div class="flex gap-2">
          <Button
            element-name="bonus-wallet"
            type="tertiary-alternative"
            rounding="md"
            @click="refreshBonusWallet"
          >
            <ArrowPathIcon class="!w-4 !h-4 aspect-square" />
          </Button>
          <div
            class="cursor-pointer rounded-lg flex items-center px-2 bg-base-secondary"
            :title="$t('bonus-wallet-how-to-icon-tooltip')"
            @click="howToClicked()"
          >
            <p
              class="my-0 text-dark-700 dark:text-light-50 mr-2 font-bold text-sm line-height-1"
            >
              {{ $t('how-to') }}
            </p>

            <ExternalIcon iconName="how-to-video-play" class="w-5 h-5" />
          </div>
        </div>
      </div>
      <hr class="w-full border-light-200 dark:border-dark-900" />
      <div class="p-2 sm:pb-4">
        <LazyUserInterfaceGenericLoader v-if="!bonus && loading" />
        <div v-else>
          <LazyAccountHistoryBonusWalletCard
            v-if="!!bonus?.activeCasinoBonus"
            :bonus="bonus?.activeCasinoBonus"
          />
          <div v-if="!!bonus?.pendingCasinoBonuses?.length">
            <LazyAccountHistoryBonusWalletCard
              v-for="(pendingBonus, index) in bonus?.pendingCasinoBonuses"
              :key="index"
              :bonus="pendingBonus"
              :is-active="false"
              @forfeit="setPotentialForfeit($event)"
            />
          </div>
        </div>
        <div
          v-if="
            !loading &&
            !bonus?.activeCasinoBonus &&
            !bonus?.pendingCasinoBonuses?.length
          "
        >
          <LazyUserInterfaceGenericError state="info">
            {{ $t('no-bonuses') }}
          </LazyUserInterfaceGenericError>
        </div>
      </div>
    </UserSettingsBaseContainer>
  </div>
</template>

<style lang="scss">
.bonus-tabs {
  [data-pc-section='navcontainer'] {
    @apply flex justify-center;
    [data-pc-section='navcontent'] {
      @apply max-w-max;
      ul {
        @apply mt-2 mb-4 my-0 p-0.5 ps-0.5 rounded-lg list-none flex justify-center aria-hidden:hidden;
      }
    }
  }
}
</style>
<style scoped lang="scss">
.allow-scroll {
  @apply max-h-[70vh] overflow-y-auto;
}
</style>
